import React, { useCallback } from 'react';
import type { MessageDescriptor } from 'react-intl';
import { defineMessages, useIntl } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import IconButton from '@atlaskit/button/icon/button';
import DropdownMenu from '@atlaskit/dropdown-menu/dropdown-menu';
import DropdownItem from '@atlaskit/dropdown-menu/dropdown-menu-item';
import DropdownItemGroup from '@atlaskit/dropdown-menu/dropdown-menu-item-group';
import type { CustomTriggerProps } from '@atlaskit/dropdown-menu/types';
import FilterIcon from '@atlaskit/icon/core/filter';
import { token } from '@atlaskit/tokens';

import { SUGGESTIONS_PANEL_ANALYTICS_SOURCE } from '../analytics.ts';
import { SuggestionFilterValues, SuggestionSortValues } from '../suggestionsPanelFilters.ts';
import { useSuggestionsPanelFilters } from '../useSuggestionsPanelFilters.ts';

const i18n = defineMessages({
	filterAndSortLabel: {
		id: 'suggestions-panel.header.filter-and-sort.label',
		defaultMessage: 'Filter and sort suggestions',
		description:
			'Accessible label and tooltip for the button that opens the filter and sort menu in the AI suggestions panel header',
	},
	filterTitle: {
		id: 'suggestions-panel.header.filter.title',
		defaultMessage: 'Filter',
		description: 'Title for the group of filter options in the AI suggestions panel filter menu',
	},
	filterAll: {
		id: 'suggestions-panel.header.filter.all',
		defaultMessage: 'All suggestions',
		description: 'Filter option that shows every suggestion in the AI suggestions panel',
	},
	filterCreatedByMe: {
		id: 'suggestions-panel.header.filter.created-by-me',
		defaultMessage: 'Created by me',
		description:
			'Filter option that only shows the suggestions the current user asked Rovo to generate',
	},
	sortTitle: {
		id: 'suggestions-panel.header.sort.title',
		defaultMessage: 'Sort',
		description: 'Title for the group of sort options in the AI suggestions panel filter menu',
	},
	sortTopToBottom: {
		id: 'suggestions-panel.header.sort.top-to-bottom',
		defaultMessage: 'Top → Bottom',
		description:
			'Sort option that orders suggestions by where they appear in the page, from the top of the page down',
	},
	sortNewToOld: {
		id: 'suggestions-panel.header.sort.new-to-old',
		defaultMessage: 'New → Old',
		description: 'Sort option that orders suggestions with the most recently created one first',
	},
});

const filterOptions = [
	{ value: SuggestionFilterValues.ALL, label: i18n.filterAll },
	{ value: SuggestionFilterValues.CREATED_BY_ME, label: i18n.filterCreatedByMe },
];

const sortOptions = [
	{ value: SuggestionSortValues.TOP_TO_BOTTOM, label: i18n.sortTopToBottom },
	{ value: SuggestionSortValues.NEW_TO_OLD, label: i18n.sortNewToOld },
];

const FILTER_ICON_TEST_ID = 'suggestions-panel-filter-icon';

type FilterIconProps = React.ComponentProps<typeof FilterIcon>;

function DefaultFilterIcon(props: FilterIconProps): React.JSX.Element {
	return <FilterIcon {...props} testId={FILTER_ICON_TEST_ID} />;
}

/**
 * `IconButton`'s own `isSelected` state doesn't render for this trigger (see the same note in
 * `CommentsPanelFilterMenu`), so a non-default filter or sort is conveyed with a tinted icon
 * instead.
 *
 * Noting: this only reaches sighted users. The selection itself stays available to assistive
 * technology through the menu items' selected state.
 */
function SelectedFilterIcon(props: FilterIconProps): React.JSX.Element {
	return (
		<FilterIcon {...props} color={token('color.icon.selected')} testId={FILTER_ICON_TEST_ID} />
	);
}

type MenuOptionProps<TValue extends string> = {
	isSelected: boolean;
	label: MessageDescriptor;
	onSelect: (value: TValue) => void;
	testId: string;
	value: TValue;
};

/** Own component so each option keeps a stable `onClick` instead of one built per render. */
const MenuOption = <TValue extends string>({
	isSelected,
	label,
	onSelect,
	testId,
	value,
}: MenuOptionProps<TValue>): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const handleClick = useCallback(() => onSelect(value), [onSelect, value]);

	return (
		<DropdownItem isSelected={isSelected} onClick={handleClick} testId={testId}>
			{formatMessage(label)}
		</DropdownItem>
	);
};

export const SuggestionsFilterMenu = (): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [{ currentFilter, currentSort }, { setCurrentFilter, setCurrentSort }] =
		useSuggestionsPanelFilters();

	const handleFilterClick = useCallback(
		(filter: SuggestionFilterValues) => {
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					source: SUGGESTIONS_PANEL_ANALYTICS_SOURCE,
					action: 'clicked',
					actionSubject: 'menuItem',
					actionSubjectId: 'suggestionsPanelFilter',
					attributes: { filter },
				},
			}).fire();

			setCurrentFilter(filter);
		},
		[createAnalyticsEvent, setCurrentFilter],
	);

	const handleSortClick = useCallback(
		(sort: SuggestionSortValues) => {
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					source: SUGGESTIONS_PANEL_ANALYTICS_SOURCE,
					action: 'clicked',
					actionSubject: 'menuItem',
					actionSubjectId: 'suggestionsPanelSort',
					attributes: { sort },
				},
			}).fire();

			setCurrentSort(sort);
		},
		[createAnalyticsEvent, setCurrentSort],
	);

	const isDefaultFilterState =
		currentFilter === SuggestionFilterValues.ALL &&
		currentSort === SuggestionSortValues.TOP_TO_BOTTOM;

	return (
		<DropdownMenu
			placement="bottom-end"
			spacing="compact"
			shouldRenderToParent
			testId="suggestions-panel-filter-menu"
			trigger={({ triggerRef, ...triggerProps }: CustomTriggerProps<HTMLButtonElement>) => (
				<IconButton
					{...triggerProps}
					ref={triggerRef}
					appearance="subtle"
					spacing="default"
					icon={isDefaultFilterState ? DefaultFilterIcon : SelectedFilterIcon}
					isTooltipDisabled={false}
					label={formatMessage(i18n.filterAndSortLabel)}
					testId="suggestions-panel-filter-button"
				/>
			)}
		>
			<DropdownItemGroup id="filter" title={formatMessage(i18n.filterTitle)}>
				{filterOptions.map(({ value, label }) => (
					<MenuOption
						key={value}
						isSelected={value === currentFilter}
						label={label}
						onSelect={handleFilterClick}
						testId={`suggestions-panel-filter-${value}`}
						value={value}
					/>
				))}
			</DropdownItemGroup>
			<DropdownItemGroup id="sort" title={formatMessage(i18n.sortTitle)}>
				{sortOptions.map(({ value, label }) => (
					<MenuOption
						key={value}
						isSelected={value === currentSort}
						label={label}
						onSelect={handleSortClick}
						testId={`suggestions-panel-sort-${value}`}
						value={value}
					/>
				))}
			</DropdownItemGroup>
		</DropdownMenu>
	);
};
